<template>
    <form
            @submit.prevent="validate"
            v-if="booking.period"
            class="card w-100"
    >
        <booking-summary
                :event="event"
                :booking="booking"
        ></booking-summary>

          <div class="card-body">
              <div class="list-group list-group-flush">
                  <div v-if="allLockersSoldOut && booking.period.lockers_sold_out_message">
                      <p>{{ booking.period.lockers_sold_out_message }}</p>
                  </div>

                  <div
                        v-for="(line, index) in booking.lines"
                        v-bind:key="index"
                        class="list-group-item list-group-item-action flex-column align-items-start"
                        @click="selectLockerSize(line)"
                  >
                    <div class="d-flex align-items-center">
                        <div
                                v-if="line.available"
                                class="locker-quantity-controls"
                        >
                            <transition
                                    name="locker-quantity-controls"
                                    mode="out-in"
                            >
                            <span v-if="showControls(line)">
                                <span
                                        class="dot"
                                        @click.stop="decreaseQuantity(line)"
                                >
                                    -
                                </span>

                                <span   v-if="inPosMode()"
                                        class="line-quantity">
                                    {{ line.quantity}}
                                </span>
                                <input  v-else
                                        v-model="line.quantity"
                                        type="number"
                                        min="0"
                                        :max="maxQuantity"
                                        class="form-control"
                                        @click.stop
                                >
                            </span>
                            </transition>

                            <span
                                    class="dot"
                                    @click.stop="increaseQuantity(line)"
                            >
                            +
                        </span>
                        </div>

                        <h5 class="text-truncate mb-0 flex-fill">
                            {{ line.name }}
                        </h5>

                        <div class="flex-fill text-right">
                            <span v-if="line.available">
                                {{ $n(line.price, 'currency') }}
                            </span>

                            <em v-else>
                                {{ $t('soldOut') }}
                            </em>
                        </div>
                    </div>

                    <small>
                        {{ line.description }}
                    </small>
                </div>
            </div>

            <small
                    v-if="booking.period.service_fee && booking.period.service_fee > 0"
                    class="d-inline-block"
            >
                {{ $t('serviceFee', [$n(booking.period.service_fee, 'currency')]) }}
            </small>

            <div class="btn-group btn-block mt-3">
                <button
                        v-if="canGoBack"
                        type="button"
                        class="btn btn-light"
                        :disabled="saving"
                        @click="redirect"
                >
                  {{ $t('back') }}
                </button>

                <template v-if="totalLockers > 0">
                    <template v-if="isCashier()">
                      <template v-if="!saving">
                        <button
                            v-if="event.cash"
                            type="submit"
                            class="btn btn-light"
                            @click="save('cash')"
                            :disabled="saving"
                        >
                          {{ $t('cash') }}
                        </button>

                        <button
                            type="submit"
                            class="btn btn-light"
                            @click="save()"
                            :disabled="saving"
                        >
                          {{ $t('PIN') }}
                        </button>

                        <button
                            v-if="event.use_reservations"
                            type="submit"
                            class="btn btn-light"
                            @click="save('reservation')"
                            :disabled="saving"
                        >
                          {{ $t('reservation') }}
                        </button>

                        <button
                            type="submit"
                            class="btn btn-light"
                            @click="save('service')"
                            :disabled="saving"
                        >
                          {{ $t('service') }}
                        </button>
                      </template>

                      <button
                          v-else
                          type="button"
                          class="btn btn-light"
                          :disabled="saving"
                      >
                        <template>
                          <span class="spinner-border" role="status" aria-hidden="true"></span>
                          {{ $t('loading') }}
                        </template>
                      </button>
                  </template>

                  <template v-else-if="inPosMode()">
                    <button
                        v-if="inPosMode()"
                        type="submit"
                        class="btn btn-primary bg-primary"
                        :disabled="saving"
                    >
                      <template v-if="!saving">
                        {{ $t('pay') }}
                      </template>

                      <template v-else>
                        <span class="spinner-border" role="status" aria-hidden="true"></span>
                        {{ $t('loading') }}
                      </template>
                    </button>
                  </template>

                  <button
                          v-else
                          type="submit"
                          class="btn btn-secondary"
                  >
                      {{ $t('continue') }}
                  </button>
                </template>
            </div>
        </div>

        <locker-company-logo></locker-company-logo>
    </form>
</template>

<script>
    import BookingSummary from '../components/BookingSummary';
    import LockerCompanyLogo from '../components/LockerCompanyLogo';
    import eventBus from '../eventBus';
    import posMixin from '../mixins/posMixin';
    import moment from 'moment';

    const maxQuantity = 10; // Default max quantity of lockers
    const redirectTimeout = 45; // Redirect timeout in seconds

    export default {
        components: {
            BookingSummary,
            LockerCompanyLogo
        },

        mixins: [posMixin],

        props: [
            'event',
            'booking',
            'saving'
        ],

        data() {
            return {
                redirectTimer: null
            }
        },

        computed: {
           /**
            * Can we go back?
            * - To periods
            * - Or to zones
            */
            canGoBack() {
                return this.multiplePeriods || this.booking.can_select_zone;
            },

          /**
           * Visible periods. (Filter periods )
           * @returns {*}
           */
            visiblePeriods() {
                if (this.inPosMode()) {
                    // Check active periods
                    return this.event.periods.filter(period => {
                        return moment(period.assign_at).isBefore() && moment(period.end).isAfter();
                    });
                }

                // All periods
                return this.event.periods;
            },

            /**
             * Does this event have multiple periods?
             */
            multiplePeriods() {
                return this.visiblePeriods.length > 1;
            },

            /**
             * Total number of lockers.
             */
            totalLockers() {
                return this.booking.totalLockers;
            },

            /**
             * Have all lockers been sold out?
             * @returns {boolean | void}
             */
            allLockersSoldOut() {
                return this.booking.lines.every((line) => {
                    return !line.available;
                });
            },

           /**
            * Maximum amount of lockers that can be booked.
            *  - If lockers are free, only 1 locker can be booked.
            *  - Otherwise use the default maximum amount of lockers
            * @returns {number}
            */
            maxQuantity() {
               if (this.inPosMode()) {
                  return maxQuantity;
               }

               return this.booking.isFree() ? 1 : maxQuantity;
            }
        },

        watch: {
            '$route': 'init',
            booking: {
                handler() {
                    this.checkIdle();
                },
                deep: true
            }
        },

        created() {
            this.init();
        },

        beforeDestroy() {
            this.stopRedirectTimer();
        },

        methods: {
            /**
             * Initialise component, check for period and idle.
             */
            init() {
                if (!this.booking.period) {
                    this.$router.push('/event/' + this.event.code);
                }

                this.checkIdle();
            },

            /**
             * Validate this form, if valid emit event 'ready' and go to next page.
             */
            validate() {
                this.stopRedirectTimer();

                eventBus.$emit('clearErrors');

                if (+this.totalLockers === 0) {
                    eventBus.$emit('error', this.$t('errors.noLockers'));
                    return;
                }

                this.$emit('ready', this.booking, this.isKiosk());

                if (!this.inPosMode()) {
                    this.$router.push('/event/' + this.event.code + '/details');
                }
            },

            /**
             * Show controls.
             * - Line has quantity.
             * - Line has no quantity set.
             *
             * @param {Object} line
             * @return Boolean
             */
            showControls(line) {
                return line.quantity > 0 || line.quantity === '';
            },

            /**
             * Increase quantity of line with one.
             *
             * @param {Object} line
             */
            increaseQuantity(line) {
                if (line.quantity === this.maxQuantity) {
                    return;
                }

                // Only 1 locker can be booked for free.
                if (this.booking.isFree() && this.booking.totalQuantity() === this.maxQuantity) {
                    return;
                }

                line.quantity++;
            },

            /**
             * Decrease quantity of line with one.
             *
             * @param {Object} line
             */
            decreaseQuantity(line) {
                if (line.quantity === 0) {
                    return;
                }

                line.quantity--;
            },

            /**
             * Select locker size.
             *
             * @param {Object} line
             */
            selectLockerSize(line) {
                if (!line.available) {
                    return;
                }

                if (+line.quantity > 0) {
                    return;
                }

                this.increaseQuantity(line);
            },

            /**
             * Check if we need to start the idle redirect.
             */
            checkIdle() {
                if (!this.inPosMode() || !this.multiplePeriods) {
                    return;
                }

                this.startRedirectTimer();
            },

            /**
             * Start timer for redirecting.
             */
            startRedirectTimer() {
                if (this.redirectTimer !== null) {
                    clearTimeout(this.redirectTimer);
                }

                this.redirectTimer = setTimeout(this.redirect, redirectTimeout * 1000);
            },

            /**
             * Stop timer for redirecting.
             */
            stopRedirectTimer() {
                if (this.redirectTimer === null) {
                    return;
                }

                clearTimeout(this.redirectTimer);

                this.redirectTimer = null;
            },

            /**
             * Redirect back to booking.
             */
            redirect() {
                if (this.booking.can_select_zone && !this.multiplePeriods) {
                    // Back to zone selection
                    this.booking.preferred_zone = null;
                }

                this.booking.reset();

                this.$router.push({
                    name: 'booking',
                    params: {
                        code: this.event.code,
                        requestedEvent: this.event
                    }
                });
            },

          /**
           * @param {String?} provider
           *
           * Save booking.
           */
          save(provider) {
            if (this.saving) {
              return;
            }

            const booking = this.booking;
            delete booking.payment_provider;

            if (provider === 'cash' || provider === 'reservation' || provider === 'service') {
              booking.payment_provider = provider;
            }

            this.$emit('ready', booking, true);
          }
        }
    }
</script>

<style lang="scss">
    .list-group-item {
        > div {
            min-height: 30px;
        }
    }
</style>
